import { Injectable, inject, DestroyRef } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';
import { map, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export enum ScreenSize {
  Mobile = 'mobile',
  Tablet = 'tablet',
  Desktop = 'desktop'
}

export interface ResponsiveState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenSize: ScreenSize;
  width: number;
}

@Injectable({
  providedIn: 'root'
})
export class ResponsiveService {
  private readonly MOBILE_BREAKPOINT = 768;
  private readonly TABLET_BREAKPOINT = 1024;
  private destroyRef = inject(DestroyRef);

  private responsiveState$ = new BehaviorSubject<ResponsiveState>(
    this.getResponsiveState()
  );

  constructor() {
    this.initResizeListener();
  }

  get state$(): Observable<ResponsiveState> {
    return this.responsiveState$.asObservable();
  }

  get currentState(): ResponsiveState {
    return this.responsiveState$.value;
  }

  get isMobile(): boolean {
    return this.currentState.isMobile;
  }

  get isTablet(): boolean {
    return this.currentState.isTablet;
  }

  get isDesktop(): boolean {
    return this.currentState.isDesktop;
  }

  private initResizeListener(): void {
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(100),
        map(() => this.getResponsiveState()),
        distinctUntilChanged((prev, curr) =>
          prev.screenSize === curr.screenSize
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(state => this.responsiveState$.next(state));
  }

  private getResponsiveState(): ResponsiveState {
    const width = window.innerWidth;
    // Mobile is <= 768 (inclusive), matching the app's dominant CSS convention
    // `@media (max-width: 768px)` and covering iPad-portrait (768px) so the
    // side-menu shows the hamburger there. Tablet is 769..1023, Desktop >= 1024.
    const isMobile = width <= this.MOBILE_BREAKPOINT;
    const isTablet = width > this.MOBILE_BREAKPOINT && width < this.TABLET_BREAKPOINT;
    const isDesktop = width >= this.TABLET_BREAKPOINT;

    let screenSize: ScreenSize;
    if (isMobile) {
      screenSize = ScreenSize.Mobile;
    } else if (isTablet) {
      screenSize = ScreenSize.Tablet;
    } else {
      screenSize = ScreenSize.Desktop;
    }

    return {
      isMobile,
      isTablet,
      isDesktop,
      screenSize,
      width
    };
  }
}
